// src/hooks/useQuizManager.ts

import { useState, useEffect, useMemo, useCallback, useContext, useId } from 'react';
import { doc, onSnapshot } from 'firebase/firestore';
import { db, appId } from '../firebase';
import shuffleArray from '../utils/shuffleArray';
import { useQuiz, getQuizDataWithCache } from '../hooks/useQuiz';
import * as quizService from '../services/quizService';
import { UserContext } from '../services/user/LoginUserContext';
import { updateChallengeQuiz, calculateReviewCount, removeReviewItemsByQuestionIds } from '../services/challengeQuizSectionService'; // removeReviewItemsByQuestionIds をインポート
import { Quiz, ReviewItem, Question, StreakData } from '../types'; // ReviewItem 型もインポート
import { updateCompletionDateList } from '../services/streakService';
import { logger } from '../utils/logger';
import { useStreak } from '../hooks/useStreak';
import { useOnlineStatus } from '../hooks/useOnlineStatus'; // カスタムフックをインポート

/**
 * クイズ本体関連の処理まとめ
 */
export const useQuizManager = ({ setCurrentPage }) => {
    const { user, loading: userContextLoading } = useContext(UserContext);

    const [activeQuiz, setActiveQuiz] = useState<Quiz | null>(null);
    const [selectedQuizId, setSelectedQuizId] = useState<string | null>(null);
    const [quizToEditId, setQuizToEditId] = useState<string | null>(null);
    const [reviewItems, setReviewItems] = useState<ReviewItem[]>([]);
    const [reviewItemsLoading, setReviewItemsLoading] = useState(true);

    const isOnline = useOnlineStatus(); // オンライン状態を取得

    const { quiz: activeLoadedQuiz, isLoading: activeQuizLoading, isError: activeQuizError } = useQuiz(selectedQuizId || '');

    // useStreakフックの戻り値の型を明示
    const { streakDays, completionDateList, mutate: streakMutate }: {
        streakDays: number;
        completionDateList: string[];
        // ここにmutateの型を記載:
        mutate: (
            data?: StreakData | Promise<StreakData> | ((currentData: StreakData | undefined) => StreakData),
            shouldRevalidate?: boolean
        ) => Promise<void | StreakData>;
    } = useStreak(user?.uid);

    // reviewItemsをFirebaseから取得し、キャッシュする
    useEffect(() => {
        let unsubscribeReviewItems: () => void;

        if (user && !userContextLoading) {

            setReviewItemsLoading(true);
            const userDocRef = doc(db, `artifacts/${appId}/users/${user.uid}/userData`, 'ChallengeQuizSelection');

            unsubscribeReviewItems = onSnapshot(userDocRef, (docSnap) => {

                if (docSnap.exists()) {
                    const data = docSnap.data();
                    setReviewItems(data.reviewItems || []);
                } else {
                    setReviewItems([]);
                }
                setReviewItemsLoading(false);
            }, (error) => {
                logger.error("Error fetching review items: ", error);
                setReviewItemsLoading(false);
            });
        } else {
            setReviewItems([]);
            setReviewItemsLoading(false);
        }
        return () => {
            if (unsubscribeReviewItems) {
                unsubscribeReviewItems();
            }
        };
    }, [user, userContextLoading]);

 // クイズ開始
    // 【修正1】startQuizを async に変更し、limitを引数に追加
    const startQuiz = useCallback(async (quizId: string, showMessage: Function, limit?: number) => {
        if (!quizId) {
            showMessage("クイズの開始に失敗しました。", () => { }, undefined, false);
            return;
        }
        
        try {
            // 【修正2】データを直接取得（startQuiz内で完結させる）
            const quiz = await getQuizDataWithCache(quizId); 

            if (!quiz) {
                // Top.tsxでリスト存在チェック済みだが、データ取得エラーとして処理
                showMessage(
                    'クイズのデータ取得に失敗しました。ホーム画面に戻ります。',
                    () => {
                        setCurrentPage('home'); 
                    },
                    null,
                    false
                );
                return;
            }

            // 成功ロジック:
            const quizCopy = JSON.parse(JSON.stringify(quiz));
            let selectedQuestions = shuffleArray(quizCopy.questions || []);
            
            // 【修正3】問題数制限ロジックを limit に基づいて修正
            const actualLimit = limit && limit > 0 ? limit : 10; 

            if (selectedQuestions.length > actualLimit) { 
                selectedQuestions = selectedQuestions.slice(0, actualLimit);
            }
            
            setActiveQuiz({ ...quizCopy, questions: selectedQuestions, isReview: false });
            // 【修正4】setSelectedQuizId(null); を削除
            setCurrentPage?.('quiz');

        } catch(error) {
             logger.error("クイズの読み込み中に致命的なエラーが発生しました:", error);
            showMessage("クイズの読み込み中にエラーが発生しました。", () => {setCurrentPage('home'); }, null, false);
        }
    }, [setCurrentPage]); // 依存配列を修正
    // 復習クイズ開始
    const startReviewQuiz = async (showMessage: Function) => {
        if (reviewItemsLoading) {
            showMessage('復習アイテムを読み込み中です。少々お待ちください。', () => { }, null, false);
            return;
        }
        if (!user) { // ユーザーがログインしていない場合は処理を中断
            showMessage('ログインして復習クイズを開始してください。', () => { }, null, false);
            return;
        }

        //対象のクイズを取得
        const now = new Date();
        const reviewEligibleItems = (reviewItems || [])
            .filter(item => item.nextReviewDate && new Date(item.nextReviewDate.seconds * 1000) <= now);
        if (reviewEligibleItems.length === 0) {
            showMessage('現在、復習する問題はありません。', () => { }, null, false);
            return;
        }

        //重複排除したquizIDのリストを作成
        const uniqueQuizIds = Array.from(new Set(reviewEligibleItems.map(item => item.quizId)));

        //クイズデータを非同期並列処理にて取得
        const quizzesDataPromises = uniqueQuizIds.map(quizId => getQuizDataWithCache(quizId));
        const loadedQuizzes = (await Promise.all(quizzesDataPromises)).filter(Boolean) as Quiz[];

        const reviewQuestions: (Question & { reviewInfo: ReviewItem })[] = [];
        const questionIdsToRemove: string[] = []; // 削除対象のquestionIdを収集する配列

        //対象のクイズ一覧から、存在しなかったクイズ（すでに消されたであろうクイズ）を削除
        reviewEligibleItems.forEach(item => {
            const quiz = loadedQuizzes.find(q => q.id === item.quizId);
            if (!quiz) {
                // クイズが見つからない場合、このreviewItemを削除対象に追加
                questionIdsToRemove.push(item.questionId);
                return;
            }
            const question = quiz.questions.find(ques => ques.id === item.questionId);
            if (!question) {
                // 質問が見つからない場合、このreviewItemを削除対象に追加
                questionIdsToRemove.push(item.questionId);
                return;
            }
            reviewQuestions.push({ ...question, reviewInfo: item });
        });

        // 見つからなかったクイズをChallengeQuizSelection(Firestore)から削除
        // 通信が不安定の場合を考慮して、削除はオンラインの場合のみ実施（↑の処理はオフラインで、ここがオンラインの時は諦める）
        if (questionIdsToRemove.length > 0 && isOnline ) {
            try {
                // サービス関数を呼び出してFirestoreを更新
                const updatedReviewItems = await removeReviewItemsByQuestionIds(user.uid, questionIdsToRemove);
                setReviewItems(updatedReviewItems); // UIの状態も更新
                showMessage(`存在しない問題に関連する${questionIdsToRemove.length}件の復習アイテムを削除しました。`, () => { }, null, false);
            } catch (error) {
                logger.error("Error removing stale review items:", error);
                showMessage('復習アイテムのクリーンアップ中にエラーが発生しました。', () => { }, null, false);
            }
        }

        if (reviewQuestions.length === 0) {
            showMessage('現在、復習する問題はありません。', () => { }, null, false);
            return;
        }

        let finalReviewQuestions = shuffleArray(reviewQuestions);
        if (finalReviewQuestions.length > 10) {
            finalReviewQuestions = finalReviewQuestions.slice(0, 10);
        }
        setActiveQuiz({
            id: 'review-quiz',
            title: 'おすすめのクイズ',
            questions: finalReviewQuestions,
            isReview: true,
            isPublic: false,         // 復習クイズは公開ではないとする
            authorId: 'review_user', // 
            authorNickname: '復習クイズ', // 
        });
        setCurrentPage?.('quiz');
    };

    const reviewCount = useMemo(() => {
        if (reviewItemsLoading) return 0;
        return calculateReviewCount(reviewItems);
    }, [reviewItems, reviewItemsLoading]);

    const deleteQuiz = async (quizId: string, showMessage: Function) => {
        showMessage('このクイズを完全に削除しますか？この操作は元に戻せません。', async () => {
            try {
                await quizService.deleteQuiz(quizId);
                showMessage('クイズを削除しました。', () => { }, null, false);
            } catch (error) {
                logger.error("Error deleting quiz: ", error);
                showMessage('クイズの削除に失敗しました。', () => { }, null, false);
            }
        });
    };

    const onQuizComplete = async (quiz: Quiz, answers: { id: string; correct: boolean; quizId: string; }[], handlePageChange: Function) => {

        if (!user) return;
        handlePageChange('home');
        if (answers.length === 0) return;

        await updateChallengeQuiz(
            user.uid,
            answers.map(({ id, correct, quizId }) => ({ id, correct, quizId })),
            reviewItems
        );
        await updateCompletionDateList(user.uid);
        streakMutate();

    };

    return {
        activeQuiz,
        reviewCount,
        startQuiz,
        startReviewQuiz,
        deleteQuiz,
        onQuizComplete,
        quizToEditId,
        setQuizToEditId,
        setActiveQuiz,
        reviewItemsLoading
    };
};